import { RotateCcw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { Button } from './ui/Button';
import { Input } from './ui/Input';

/* Per-customer document overrides (#2). Lives in the customer profile (and reused at
 * onboarding). Stores a small patch per document type on top of the GLOBAL/segment template;
 * here we expose the fields a client most often wants branded. */

type DocType = 'INVOICE' | 'QUOTE' | 'CREDIT_NOTE' | 'DELIVERY_NOTE';
interface Patch {
  title?: string;
  style?: { accent?: string };
  labels?: Record<string, string>;
}
// Labels: domain.documentType.<key>.
const DOC_TYPES: DocType[] = ['INVOICE', 'QUOTE', 'CREDIT_NOTE', 'DELIVERY_NOTE'];

export default function CustomerDocumentOverrides({
  customerId,
  canEdit,
}: {
  customerId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [docType, setDocType] = useState<DocType>('INVOICE');
  const [overrides, setOverrides] = useState<Record<string, Patch>>({});
  const [draft, setDraft] = useState<Patch>({});

  const reload = async () => {
    try {
      const { data } = await apiClient.get<Record<string, Patch>>(
        `/customers/${customerId}/document-overrides`
      );
      setOverrides(data);
      setDraft(data[docType] ?? {});
    } catch (err) {
      showApiError(err);
    }
  };

  useEffect(() => {
    void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [customerId]);
  useEffect(() => {
    setDraft(overrides[docType] ?? {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [docType]);

  const set = (p: Patch) => setDraft(p);
  const hasOverride = Boolean(overrides[docType]);

  const save = async () => {
    try {
      const { data } = await apiClient.put<Record<string, Patch>>(
        `/customers/${customerId}/document-overrides/${docType}`,
        draft,
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setOverrides(data);
      push({ tone: 'success', message: t('relations.docOverrides.toastSaved') });
    } catch (err) {
      showApiError(err);
    }
  };
  const remove = async () => {
    try {
      const { data } = await apiClient.delete<Record<string, Patch>>(
        `/customers/${customerId}/document-overrides/${docType}`,
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setOverrides(data);
      setDraft({});
      push({ tone: 'success', message: t('relations.docOverrides.toastRemoved') });
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="space-y-4 text-sm max-w-xl">
      <p className="text-muted-foreground text-xs">{t('relations.docOverrides.intro')}</p>

      <div className="flex rounded border overflow-hidden w-fit">
        {DOC_TYPES.map((d) => (
          <button
            key={d}
            onClick={() => setDocType(d)}
            className={`px-3 py-1 ${docType === d ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
          >
            {t(`domain.documentType.${d}`)}
            {overrides[d] ? ' •' : ''}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="block text-xs text-muted-foreground mb-1">
          {t('relations.docOverrides.documentTitle')}
        </span>
        <Input
          disabled={!canEdit}
          placeholder={t('relations.docOverrides.standard')}
          value={draft.title ?? ''}
          onChange={(e) => set({ ...draft, title: e.target.value || undefined })}
        />
      </label>

      <label className="block">
        <span className="block text-xs text-muted-foreground mb-1">
          {t('relations.docOverrides.accentColour')}
        </span>
        <Input
          type="color"
          className="h-9 w-16 cursor-pointer p-1"
          disabled={!canEdit}
          value={draft.style?.accent ?? '#2a4b8d'}
          onChange={(e) => set({ ...draft, style: { accent: e.target.value } })}
        />
      </label>

      <label className="block">
        <span className="block text-xs text-muted-foreground mb-1">
          {t('relations.docOverrides.clientLabel')}
        </span>
        <Input
          disabled={!canEdit}
          placeholder={t('relations.docOverrides.standard')}
          value={draft.labels?.client ?? ''}
          onChange={(e) =>
            set({ ...draft, labels: { ...(draft.labels ?? {}), client: e.target.value } })
          }
        />
      </label>

      {canEdit && (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={!hasOverride} onClick={remove}>
            <RotateCcw className="h-4 w-4" />
            {t('relations.docOverrides.remove')}
          </Button>
          <Button variant="success" size="sm" onClick={save}>
            <Save className="h-4 w-4" />
            {t('relations.docOverrides.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
